"use client";

import { motion } from "framer-motion";
import { GraduationCap, Briefcase, Plane, Home, Building2, Banknote, FileText } from "lucide-react";

const services = [
  {
    icon: <GraduationCap size={22} className="text-[#B8893A]" />,
    title: "Define Your Business Vision",
    description:
      "Every successful company starts with clarity. We help you identify the right business activity and license type aligned with UAE regulations and your long-term goals.",
  },
  {
    icon: <Briefcase size={22} className="text-[#B8893A]" />,
    title: "Choose the Right Setup Structure",
    description:
      "Not all jurisdictions are the same. We guide you to the best option: Mainland for full UAE market access, Free Zone for 100% ownership and tax efficiency, Offshore for international operations.",
  },
  {
    icon: <Plane size={22} className="text-[#B8893A]" />,
    title: "Secure Your Trade Name",
    description:
      "Your brand matters. We ensure your company name is unique, compliant, and approved quickly, avoiding delays.",
  },
  {
    icon: <Home size={22} className="text-[#B8893A]" />,
    title: "Get Instant Government Approval",
    description:
      "We handle all submissions to secure your initial approval, confirming your business is ready to proceed legally.",
  },
  {
    icon: <Building2 size={22} className="text-[#B8893A]" />,
    title: "Set Up Your Business Address",
    description:
      "Whether you need a flexi-desk, co-working space, or private office, we provide the right solution to match your license and budget.",
  },
  {
    icon: <FileText size={22} className="text-[#B8893A]" />,
    title: "Obtain Your Business License",
    description:
      "This is where your business becomes official. We manage documentation, approvals, and processing ensuring fast and smooth license issuance.",
  },
  {
    icon: <GraduationCap size={22} className="text-[#B8893A]" />,
    title: "Process Your UAE Visas",
    description:
      "We take care of everything: Investor/Owner visas, Employee visas, Family/dependent visas. So you and your team can operate legally in the UAE.",
  },
  {
    icon: <Banknote size={22} className="text-[#B8893A]" />,
    title: "Open Your Corporate Bank Account",
    description:
      "We assist you in opening a reliable UAE business bank account, ensuring seamless financial operations from day one.",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      className="relative bg-[#fafafa] py-12 sm:py-16 lg:py-20 xl:py-24 px-4 overflow-visible"
    >
      <div className="main-container overflow-visible">

        {/* ── DESKTOP LAYOUT ── */}
        <div className="hidden md:flex gap-20 items-start">

          {/* LEFT — sticky, stays fixed until right column fully scrolled */}
          <div className="w-[38%] sticky top-30 self-start">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#B8893A]">
              The Process
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl 2xl:text-6xl font-black text-[#000000] mt-3 leading-tight">
              The Process of Business Setup in the UAE
            </h2>
            <p className="text-black/80 mt-5 text-base sm:text-lg 2xl:text-xl leading-relaxed">
              A simple, transparent, and guided journey from idea to fully
              operational business.
            </p>

            <div className="mt-8 w-12 h-px bg-[#B8893A] opacity-40" />

            <p className="mt-6 text-sm sm:text-base 2xl:text-lg text-black/80 leading-relaxed">
              Our experts handle every step so you can focus on what matters —
              growing your business.
            </p>

            <button className="mt-8 bg-[#B8893A] hover:bg-[#A67B34] text-white text-sm font-semibold px-7 py-3 rounded-xl transition-all hover:scale-105">
              Get Free Consultation
            </button>
          </div>

          {/* RIGHT — scrollable list, controls how long left stays sticky */}
          <div className="w-[62%]">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5 }}
                className="flex gap-5 py-8 border-b border-gray-100 last:border-none hover:shadow-lg rounded-2xl transition-all duration-300 hover:px-3"
              >
                {/* ICON + NUMBER */}
                <div className="flex flex-col items-center gap-2 pt-1 min-w-[40px]">
                  <div className="w-12 h-12 rounded-full border border-[#B8893A]/30 bg-[#B8893A]/5 flex items-center justify-center">
                    {service.icon}
                  </div>
                  <span className="text-sm font-semibold text-[#B8893A]/40">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>

                {/* TEXT */}
                <div className="flex-1 pt-1">
                  <h3 className="text-xl font-bold text-black mb-3">
                    {service.title}
                  </h3>
                  <p className="text-black text-sm sm:text-base leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ── MOBILE LAYOUT ── */}
        <div className="md:hidden">
          <div className="text-left mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#B8893A]">
              The Process
            </span>
            <h2 className="text-3xl font-black text-[#000000] mt-2 leading-tight">
              Business Setup in the UAE
            </h2>
            <p className="text-gray-500 mt-3 text-sm">
              A simple, transparent, and guided journey from idea to fully
              operational business.
            </p>
          </div>

          <div className="flex flex-col gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
                className="flex gap-4"
              >
                <div className="flex flex-col items-center gap-2 pt-1 min-w-[36px]">
                  <div className="w-9 h-9 rounded-full border border-[#B8893A]/30 bg-[#B8893A]/5 flex items-center justify-center">
                    {service.icon}
                  </div>
                  <span className="text-xs font-semibold text-[#B8893A]/40">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="text-base font-semibold text-black mb-1">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-left mt-10">
            <button className="bg-[#B8893A] hover:bg-[#A67B34] text-white text-sm font-semibold px-8 py-3 rounded-xl transition-all hover:scale-105">
              Get Free Consultation
            </button>
          </div>
        </div>

      </div>

      {/* BACKGROUND GLOW */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-[#B8893A]/5 blur-3xl rounded-full -z-10" />
      <div className="absolute bottom-0 right-0 w-72 h-72 bg-[#B8893A]/5 blur-3xl rounded-full -z-10" />
    </section>
  );
}
